/**
 * SiteFooter - footer principal do site público.
 * Identidade Visual eBuz v1.0 (Manual seção 11 - footer hero pattern).
 *
 * - Background ink-0 (preto), grid-bg motif sutil (.04 opacity).
 * - Hero giant: "Do que você sabe ao que existe no mundo." com GradientText no fim.
 * - 4 colunas de links + linha legal.
 *
 * Server Component (sem state).
 *
 * POR QUE OS TEXTOS TEM COR INLINE
 * ================================
 * O CSS do blog traz regras NAO-LAYERED (`h1,h2,h3,h4 { color: var(--fg1) }` e
 * equivalente pra `p`). No Tailwind v4 uma regra fora de @layer vence utility,
 * entao `text-white` perdia e o rodape inteiro renderizava em rgb(74,74,82)
 * sobre fundo rgb(10,10,11) nas 118 paginas do blog: ilegivel, e ninguem via
 * porque na home ficava certo. Estilo inline vence a folha e resolve sem tocar
 * no CSS do blog. Medido em 2026-09-17 com getComputedStyle.
 */

import Link from 'next/link'
import Image from 'next/image'
import { GradientText, GridBg } from '@/lib/brand'
import { ENDERECO_EBUZ_LINHA } from '@/lib/brand/endereco'

const PROJETO = [
  { label: 'Manifesto', href: '/#manifesto' },
  { label: 'Implantação', href: '/#implantacao' },
  { label: 'Mapa de Contexto', href: '/mapa-de-contexto' },
  { label: 'Quero aplicar', href: '/#aplicacao' },
]

const RECURSOS = [
  { label: 'Livro', href: '/livro' },
  { label: 'Máquina de Livros', href: '/maquina-livros' },
  { label: 'eBook grátis', href: '/ebook' },
  { label: 'Blog', href: '/blog' },
  { label: 'Manual de Marca', href: '/brand' },
]

const EMPRESA = [
  { label: 'Sobre nós', href: '/sobre' },
  { label: 'Contato', href: '/contato' },
  { label: 'Evento', href: '/evento' },
]

// 'Direitos LGPD' apontava pra /privacidade/dpo, que devolve 404 desde sempre.
// A pagina viva e /lgpd. 'Documentacao' saiu: /c/ebuz/docs exige login, entao
// era um link de rodape publico que caia no magic link.
const LEGAL = [
  { label: 'Política de Privacidade', href: '/privacidade' },
  { label: 'Termos de Uso', href: '/termos' },
  { label: 'Direitos LGPD', href: '/lgpd' },
]

// Workspace branding info (passed when whitelabel)
export interface FooterWorkspaceInfo {
  name: string                     // ex: "Paty Shiatsu"
  tagline?: string                 // ex: "Formacao em Shiatsu pelo Metodo M.A.O.S."
  websiteUrl?: string              // ex: "https://patyshiatsu.com.br"
  logoUrl?: string                 // ex: "/email-assets/header-logo.png"
  legal?: {
    cnpj?: string
    address?: string
  }
  socialLinks?: {
    instagram?: string
    youtube?: string
    whatsapp?: string
    tiktok?: string
  }
}

interface SiteFooterProps {
  workspace?: FooterWorkspaceInfo
}

export function SiteFooter({ workspace }: SiteFooterProps = {}) {
  // Modo whitelabel: render minimalista paty (sem hero gigante, sem 4 colunas eBuz)
  if (workspace) {
    return (
      <footer className="relative bg-ink-0 text-white overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mb-8">
            <div className="flex items-center gap-3">
              {workspace.logoUrl && (
                <Image src={workspace.logoUrl} alt={workspace.name} width={120} height={32}
                       className="h-7 w-auto" priority unoptimized={workspace.logoUrl.startsWith("http")} />
              )}
              <div>
                <p className="text-[14px] font-semibold text-white" style={{ color: '#fff' }}>{workspace.name}</p>
                {workspace.tagline && <p className="text-[12px] text-white/60" style={{ color: 'rgba(255,255,255,.6)' }}>{workspace.tagline}</p>}
              </div>
            </div>
            {workspace.socialLinks && (
              <div className="flex items-center gap-4">
                {workspace.socialLinks.instagram && (
                  <a href={workspace.socialLinks.instagram} target="_blank" rel="noopener noreferrer"
                     aria-label="Instagram" className="text-white/60 hover:text-white inline-grid place-items-center">
                    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
                      <rect x={3} y={3} width={18} height={18} rx={5} />
                      <circle cx={12} cy={12} r={4} />
                      <circle cx={17.5} cy={6.5} r={0.5} fill="currentColor" />
                    </svg>
                  </a>
                )}
                {workspace.socialLinks.youtube && (
                  <a href={workspace.socialLinks.youtube} target="_blank" rel="noopener noreferrer"
                     aria-label="YouTube" className="text-white/60 hover:text-white inline-grid place-items-center">
                    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
                      <rect x={2} y={6} width={20} height={12} rx={3} />
                      <path d="M10 9.5v5l5-2.5-5-2.5z" fill="currentColor" />
                    </svg>
                  </a>
                )}
                {workspace.socialLinks.tiktok && (
                  <a href={workspace.socialLinks.tiktok} target="_blank" rel="noopener noreferrer"
                     aria-label="TikTok" className="text-white/60 hover:text-white inline-grid place-items-center">
                    <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43V9.31a8.16 8.16 0 003.77.92V6.69h-.84z" />
                    </svg>
                  </a>
                )}
                {workspace.socialLinks.whatsapp && (
                  <a href={workspace.socialLinks.whatsapp} target="_blank" rel="noopener noreferrer"
                     aria-label="WhatsApp" className="text-white/60 hover:text-white inline-grid place-items-center">
                    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 21l1.7-5.1A8 8 0 1112 20a8 8 0 01-4-1.1L3 21z" />
                    </svg>
                  </a>
                )}
              </div>
            )}
          </div>
          <div className="pt-6 border-t border-white/10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-[12px] text-white/55">
            <div className="space-y-1">
              {workspace.legal?.cnpj && <p style={{ color: 'rgba(255,255,255,.55)' }}>CNPJ {workspace.legal.cnpj}</p>}
              {workspace.legal?.address && <p style={{ color: 'rgba(255,255,255,.55)' }}>{workspace.legal.address}</p>}
            </div>
            <div className="flex sm:justify-center gap-4">
              <Link href="/privacidade" className="hover:text-white">Privacidade</Link>
              <Link href="/termos" className="hover:text-white">Termos</Link>
            </div>
            <p className="sm:text-right font-mono uppercase tracking-wider text-white/40" style={{ color: 'rgba(255,255,255,.4)' }}>
              &copy; {new Date().getFullYear()} {workspace.name}
            </p>
          </div>
        </div>
      </footer>
    )
  }

  // Default: footer eBuz completo
  return (
    <footer className="relative bg-ink-0 text-white overflow-hidden">
      {/* Grid bg motif (DNA visual) */}
      <GridBg className="absolute inset-0 pointer-events-none" style={{ opacity: 0.04 }} />

      {/* Glow rosa difuso top-right (Manual: brand presence) */}
      <div
        className="pointer-events-none absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full opacity-20 blur-3xl"
        style={{ background: 'var(--brand-grad)' }}
        aria-hidden="true"
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        {/* Hero giant */}
        <div className="mb-12 sm:mb-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-white/50 mb-4">
            + ebuz negócios digitais
          </div>
          <h2 className="font-display font-bold leading-[0.95] tracking-[-0.035em] text-balance text-white"
              style={{ fontSize: 'clamp(40px, 7vw, 96px)', color: '#fff' }}>
            Do que você sabe<br />
            ao que <GradientText>existe no mundo</GradientText>.
          </h2>
          <p className="mt-5 text-white/65 text-[14px] sm:text-[15px] max-w-xl leading-relaxed" style={{ color: 'rgba(255,255,255,.65)' }}>
            Organizamos sua experiência em uma tese, materializamos essa tese em um manifesto e acompanhamos o projeto até ele operar.
          </p>
        </div>

        {/* 4 colunas de links */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 sm:gap-10 mb-12 sm:mb-16 pt-10 border-t border-white/10">
          <div className="col-span-2 sm:col-span-1">
            <Image
              src="/favicon-ebuz.png"
              alt="eBuz"
              width={40}
              height={40}
              className="h-10 w-10 mb-3"
            />
            <p className="text-[13px] font-semibold text-white mb-1.5" style={{ color: '#fff' }}>eBuz Negócios Digitais</p>
            <p className="text-[12px] text-white/50 leading-relaxed" style={{ color: 'rgba(255,255,255,.5)' }}>
              Pessoas. Ideias. Projetos que deixam legado.
            </p>
          </div>

          <FooterColumn title="O projeto" items={PROJETO} />
          <FooterColumn title="Recursos" items={RECURSOS} />
          <FooterColumn title="Empresa" items={EMPRESA} />
          <FooterColumn title="Legal" items={LEGAL} />
        </div>

        {/* Linha legal */}
        <div className="pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-[11px] text-white/40 leading-relaxed" style={{ color: 'rgba(255,255,255,.4)' }}>
            CNPJ 29.436.457/0001-25 · {ENDERECO_EBUZ_LINHA}
          </p>
          <p className="text-[11px] text-white/40 font-mono uppercase tracking-wider whitespace-nowrap" style={{ color: 'rgba(255,255,255,.4)' }}>
            &copy; {new Date().getFullYear()} ebuz
          </p>
        </div>
      </div>
    </footer>
  )
}

interface FooterColumnProps {
  title: string
  items: Array<{ label: string; href: string; external?: boolean }>
}

function FooterColumn({ title, items }: FooterColumnProps) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-white/50 mb-3" style={{ color: 'rgba(255,255,255,.5)' }}>{title}</p>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item.href}>
            {item.external ? (
              <a
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[13px] text-white/70 hover:text-white transition-colors"
              >
                {item.label}
              </a>
            ) : (
              <Link href={item.href} className="text-[13px] text-white/70 hover:text-white transition-colors">
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
